import { useMemo } from "react";

const DATE_CANDIDATE_FIELDS = [
  "createdAt",
  "createdDate",
  "created",
  "date",
  "gameDate",
  "giveawayDate",
  "completedAt",
  "finishedAt",
  "updatedAt",
  "timestamp",
];

function toValidDate(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
}

function parseSlashOrDashDate(text) {
  const match = text.match(
    /(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?)?/i,
  );
  if (!match) return null;

  let first = Number(match[1]);
  let second = Number(match[2]);
  let year = Number(match[3]);
  let hour = Number(match[4] || 0);
  const minute = Number(match[5] || 0);
  const secondPart = Number(match[6] || 0);
  const meridiem = (match[7] || "").toUpperCase();

  if (year < 100) {
    year += 2000;
  }

  let month = first;
  let day = second;

  // If one side cannot be a month, use day/month interpretation.
  if (first > 12 && second <= 12) {
    day = first;
    month = second;
  }

  if (meridiem === "PM" && hour < 12) hour += 12;
  if (meridiem === "AM" && hour === 12) hour = 0;

  const candidate = new Date(year, month - 1, day, hour, minute, secondPart);
  if (
    candidate.getFullYear() !== year ||
    candidate.getMonth() !== month - 1 ||
    candidate.getDate() !== day
  ) {
    return null;
  }

  return candidate;
}

function extractDateFromText(text) {
  if (!text || typeof text !== "string") return null;

  const direct = toValidDate(text);
  if (direct) return direct;

  const withoutPrefix = text.replace(/^giveaway\s*/i, "").trim();
  const fromWithoutPrefix = toValidDate(withoutPrefix);
  if (fromWithoutPrefix) return fromWithoutPrefix;

  const isoMatch = text.match(/\d{4}-\d{1,2}-\d{1,2}(?:[ T]\d{1,2}:\d{2}(?::\d{2})?)?/);
  if (isoMatch) {
    const fromIso = toValidDate(isoMatch[0]);
    if (fromIso) return fromIso;
  }

  return parseSlashOrDashDate(text);
}

function resolveGameDate(game) {
  for (const field of DATE_CANDIDATE_FIELDS) {
    const candidate = game?.[field];
    const parsed = toValidDate(candidate);
    if (parsed) return parsed;
  }

  const titleDate = extractDateFromText(game?.title || game?.name || "");
  if (titleDate) return titleDate;

  return null;
}

function formatTickDate(date) {
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

export default function ParticipantsGraph({ historyGames }) {
  const graphData = useMemo(() => {
    const games = Array.isArray(historyGames) ? historyGames : [];
    const dated = games
      .map((game, index) => {
        const date = resolveGameDate(game);
        if (!date) return null;
        return {
          key: `${game?.id || game?.gameId || index}-${date.toISOString()}`,
          participants: Array.isArray(game?.participants) ? game.participants.length : 0,
          date,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    return dated;
  }, [historyGames]);

  if (graphData.length === 0) {
    return (
      <div
        style={{
          marginTop: 16,
          borderRadius: 16,
          border: "1px dashed rgba(144,224,239,0.35)",
          background: "rgba(8, 24, 50, 0.55)",
          padding: 18,
          textAlign: "center",
          color: "rgba(224,247,250,0.78)",
        }}
      >
        No dated giveaway data available for participant graph.
      </div>
    );
  }

  const width = 940;
  const height = 280;
  const margin = { top: 20, right: 24, bottom: 50, left: 52 };
  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;

  const maxParticipants = Math.max(...graphData.map((point) => point.participants), 1);
  const yMax = Math.max(2, Math.ceil(maxParticipants / 2) * 2);

  const pointCount = graphData.length;
  const toX = (index) => {
    if (pointCount === 1) return margin.left + chartWidth / 2;
    return margin.left + (index / (pointCount - 1)) * chartWidth;
  };
  const toY = (value) => margin.top + chartHeight - (value / yMax) * chartHeight;

  const points = graphData.map((point, index) => ({
    ...point,
    x: toX(index),
    y: toY(point.participants),
  }));

  const linePath = points
    .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`)
    .join(" ");

  const yTicks = Array.from({ length: 5 }, (_, idx) => Math.round((yMax / 4) * idx));
  const maxXTicks = 6;
  const xTickIndices = Array.from({ length: Math.min(maxXTicks, pointCount) }, (_, idx) => {
    if (pointCount === 1) return 0;
    return Math.round((idx * (pointCount - 1)) / (Math.min(maxXTicks, pointCount) - 1));
  }).filter((value, index, arr) => arr.indexOf(value) === index);

  return (
    <div
      style={{
        marginTop: 16,
        borderRadius: 18,
        padding: 14,
        background: "linear-gradient(180deg, rgba(8, 24, 50, 0.92), rgba(5, 16, 34, 0.88))",
        border: "1px solid rgba(80, 180, 255, 0.24)",
      }}
    >
      <div style={{ fontWeight: 700, color: "#90e0ef", marginBottom: 10 }}>Participants Per Giveaway</div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        style={{ height: "auto", display: "block" }}
        role="img"
        aria-label="Participants per giveaway over time"
      >
        <defs>
          <linearGradient id="participants-line-gradient" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#66e3ff" />
            <stop offset="100%" stopColor="#ffea9a" />
          </linearGradient>
        </defs>

        {yTicks.map((tick) => {
          const y = toY(tick);
          return (
            <g key={`y-${tick}`}>
              <line x1={margin.left} x2={width - margin.right} y1={y} y2={y} stroke="rgba(144,224,239,0.14)" strokeWidth="1" />
              <text x={margin.left - 8} y={y + 4} fill="rgba(224,247,250,0.82)" fontSize="11" textAnchor="end">
                {tick}
              </text>
            </g>
          );
        })}

        <line x1={margin.left} x2={margin.left} y1={margin.top} y2={height - margin.bottom} stroke="rgba(144,224,239,0.25)" strokeWidth="1" />
        <line x1={margin.left} x2={width - margin.right} y1={height - margin.bottom} y2={height - margin.bottom} stroke="rgba(144,224,239,0.25)" strokeWidth="1" />

        <path d={linePath} fill="none" stroke="url(#participants-line-gradient)" strokeWidth="3" strokeLinecap="round" />

        {points.map((point) => (
          <g key={point.key}>
            <circle cx={point.x} cy={point.y} r="4" fill="#0b1f3d" stroke="#8ff0ff" strokeWidth="2" />
            <title>{`${formatTickDate(point.date)}: ${point.participants} participants`}</title>
          </g>
        ))}

        {xTickIndices.map((pointIndex) => {
          const point = points[pointIndex];
          return (
            <text
              key={`x-${point.key}`}
              x={point.x}
              y={height - margin.bottom + 22}
              fill="rgba(224,247,250,0.82)"
              fontSize="11"
              textAnchor="middle"
            >
              {formatTickDate(point.date)}
            </text>
          );
        })}

        <text
          x={width / 2}
          y={height - 10}
          fill="rgba(224,247,250,0.85)"
          fontSize="12"
          textAnchor="middle"
          fontWeight="700"
        >
          Date
        </text>

        <text
          x={16}
          y={height / 2}
          fill="rgba(224,247,250,0.85)"
          fontSize="12"
          textAnchor="middle"
          fontWeight="700"
          transform={`rotate(-90 16 ${height / 2})`}
        >
          Participants
        </text>
      </svg>
    </div>
  );
}
